import Avatar from '@core/ui/static/images/user-placeholder.svg';
import moment from 'moment';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useRef } from 'react';
import { FaList } from 'react-icons/fa';
import { IoIosCall, IoMdDesktop, IoMdMail } from 'react-icons/io';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { formatPhoneNumber } from 'react-phone-number-input/max';
import { Col, Grid as BaseGrid, Row } from 'react-styled-flexboxgrid';
import Alert from 'reusecore/src/elements/Alert';
import Button from '../../../reusecore/src/elements/Button';
import ProActionLoader from '../../components/Loader/ProActionLoader';
import ProDescriptionLoader from '../../components/Loader/ProDescriptionLoader';
import ProHoursLoader from '../../components/Loader/ProHoursLoader';
import ProInfosLoader from '../../components/Loader/ProInfosLoader';
import ProLoader from '../../components/Loader/ProLoader';
import {
  Image,
  ImageWrapper,
} from '../../components/UserInfoBox/userInfoBox.style';
import { Counter } from '../../helpers/Counter';
import { db } from '../../helpers/firebaseHelper';
import { AVATAR_MEDIUM, getPhoto } from '../../helpers/getPhotos';
import useMediaQuery from '../../helpers/mediaQuery';
import Map from './Map';
import UserPost from './UserPost';
import UserContact from './UserContact';
import styled from 'styled-components';
import UserReviews from '../User/UserReviews';
import { MdComment } from 'react-icons/md';

const WEEKDAYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const Grid = styled(BaseGrid)`
  padding-right: 15px;
  padding-left: 15px;
`;

const ProPage = props => {
  const { data: { pro = {} } = {}, error, loading } = props;

  const { t } = useTranslation();

  const userContact = useRef(null);
  const userPosts = useRef(null);
  const userReviewsRef = useRef(null);

  const isMobile = useMediaQuery('(max-width: 600px)');
  const isTablet = useMediaQuery('(max-width: 1023px)');

  const {
    address = {},
    alternateName,
    description,
    image,
    imageProfil: avatar,
    geo,
    uid,
    schedule,
    website,
  } = pro;

  const {
    addressLocality,
    email,
    hoursAvailable = [],
    postalCode,
    postOfficeBoxNumber,
    streetAddress,
    telephone,
  } = address || {};

  let proAvatar = avatar ? { contentUrl: avatar } : null;
  if (image) {
    proAvatar = getPhoto(image, AVATAR_MEDIUM);
  }

  const { contentUrl, height = null, source = null, thumbnail, width = null } =
    proAvatar || {};

  const {
    contentUrl: thumbnailContentUrl,
    height: thumbnailHeight = null,
    source: thumbnailSource = null,
    width: thumbnailWidth = null,
  } = thumbnail || {};

  const imgSrc = thumbnail ? thumbnailContentUrl : contentUrl;
  const imgHeight = thumbnail ? thumbnailHeight : height;
  const imgSource = thumbnail ? thumbnailSource : source;
  const imgWidth = thumbnail ? thumbnailWidth : width;

  const getHoursAvailable = () => {
    if (
      hoursAvailable &&
      hoursAvailable instanceof Array &&
      hoursAvailable.length > 0
    ) {
      return (
        <>
          {hoursAvailable.map(hourSpecification => {
            // get week day for translation
            const dayPosition = WEEKDAYS.findIndex(
              day => day === hourSpecification.dayOfWeek
            );
            const weekday = moment.weekdays(dayPosition);

            // get opening hours
            let hours = t('pro.openingHours.closed');
            if (hourSpecification.opens && hourSpecification.closes) {
              const splitOpens = hourSpecification.opens.split(',');
              const splitCloses = hourSpecification.closes.split(',');

              const len = splitOpens.length;

              let listHours = [];
              for (let i = 0; i < len; i++) {
                listHours = [
                  ...listHours,
                  splitOpens[i] + '-' + splitCloses[i],
                ];
              }
              hours = listHours.join(' / ');
            }
            return (
              <div
                key={weekday}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  lineHeight: '30px',
                }}
              >
                <span>{weekday}</span>
                <span>{hours}</span>
              </div>
            );
          })}
        </>
      );
    } else if (schedule && schedule !== '') {
      return <p>{schedule}</p>;
    }

    return null;
  };

  const incrementField = field => {
    // Initialize the sharded counter.
    const statCounter = new Counter(
      db.collection('users').doc(uid),
      'stats.' + field
    );

    // Increment the field of the document
    statCounter.incrementBy(1);
  };

  const Loader = () => (
    <>
      <Row>
        <Col xs={12} sm={8} md={5}>
          <ProLoader />
        </Col>
        <Col xs={12} sm={4} md={4}>
          <ProInfosLoader />
        </Col>
        <Col xs={12} sm={4} md={3}>
          <ProActionLoader />
        </Col>
      </Row>
      <Row>
        <Col xs={12} sm={12} md={6}>
          <ProDescriptionLoader />
        </Col>
        <Col xs={12} sm={12} md={1} style={{ height: 30 }} />
        <Col xs={12} sm={12} md={5}>
          <ProHoursLoader />
        </Col>
      </Row>
    </>
  );

  useEffect(() => {
    if (uid) {
      // Initialize the sharded counter.
      const visits = new Counter(
        db.collection('users').doc(uid),
        'stats.visits'
      );

      // Increment the field "visits" of the document
      visits.incrementBy(1);
    }
  }, [uid]);

  if (error) return <Alert>{`Error! ${error.message}`}</Alert>;

  return (
    <div>
      <Map geo={geo} />
      <Grid>
        {loading ? (
          <Loader />
        ) : (
          <div
            style={{
              backgroundColor: '#F9F9F9',
              position: 'relative',
              borderTopLeftRadius: 15,
              borderTopRightRadius: 15,
              marginTop: -50,
              paddingTop: '2rem',
              zIndex: 1,
              paddingLeft: '2rem',
              paddingRight: '2rem',
            }}
          >
            <Row>
              <Col xs={12} sm={8} md={5}>
                <Row style={{ color: '#002F3F' }}>
                  <Col xs={4} style={{ padding: 0 }}>
                    <ImageWrapper imgWidth={112} imgHeight={112} imgRadius={85}>
                      {(proAvatar && (
                        <picture>
                          {imgSource}
                          <Image
                            src={imgSrc}
                            alt={alternateName}
                            width={imgWidth}
                            height={imgHeight}
                          />
                        </picture>
                      )) || (
                        <Image
                          src={Avatar}
                          alt="PlaceHolder Image"
                          width="112px"
                          height="112px"
                        />
                      )}
                    </ImageWrapper>
                  </Col>
                  <Col xs={8} style={{ paddingTop: 7 }}>
                    <h2>{alternateName}</h2>
                    {address && (
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span>{streetAddress}</span>
                        {postOfficeBoxNumber && (
                          <span>{postOfficeBoxNumber}</span>
                        )}
                        <span>{postalCode + ' ' + addressLocality}</span>
                      </div>
                    )}
                  </Col>
                </Row>
              </Col>
              <Col
                xs={12}
                sm={4}
                md={4}
                style={{ paddingTop: isMobile ? 20 : 49 }}
              >
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {telephone && isValidPhoneNumber(telephone) && (
                    <a
                      style={{ marginTop: 10 }}
                      href={'tel:' + telephone}
                      onClick={() => incrementField('telephones')}
                    >
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          color: '#002F3F',
                        }}
                      >
                        <IoIosCall
                          size={20}
                          style={{ color: '#00BCD4', marginRight: 5 }}
                        />
                        {formatPhoneNumber(telephone)}
                      </span>
                    </a>
                  )}
                  {website && (
                    <a
                      style={{ marginTop: 10 }}
                      href={website}
                      rel="noopener nofollow"
                      target="_blank"
                      onClick={() => incrementField('websites')}
                    >
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          color: '#002F3F',
                        }}
                      >
                        <IoMdDesktop
                          size={20}
                          style={{ color: '#00BCD4', marginRight: 5 }}
                        />
                        {website}
                      </span>
                    </a>
                  )}
                  {email && (
                    <a
                      style={{ marginTop: 10 }}
                      href={'mailto:' + email}
                      onClick={() => incrementField('emails')}
                    >
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          color: '#002F3F',
                        }}
                      >
                        <IoMdMail
                          size={20}
                          style={{ color: '#00BCD4', marginRight: 5 }}
                        />
                        {email}
                      </span>
                    </a>
                  )}
                </div>
              </Col>
              <Col
                xs={12}
                sm={4}
                md={3}
                style={{ paddingTop: isMobile || isTablet ? 42 : 0 }}
              >
                <Button
                  iconPosition="left"
                  title={t('pro.actions.contact')}
                  fontSize={12}
                  className="primary"
                  bg="#00BCD4"
                  style={{
                    marginBottom: 15,
                    width: '100%',
                    marginRight: 10,
                    padding: 15,
                    fontSize: 15,
                    fontWeight: 'bold',
                  }}
                  icon={<IoMdMail size={20} />}
                  onClick={() =>
                    userContact && userContact.current
                      ? userContact.current.scrollIntoView({
                          block: 'start',
                          inline: 'nearest',
                          behavior: 'smooth',
                        })
                      : null
                  }
                />
                <Button
                  title={t('pro.actions.ourAds')}
                  fontSize={12}
                  icon={
                    <FaList
                      size={15}
                      color="#fff"
                      style={{ marginRight: 10 }}
                    />
                  }
                  iconPosition="left"
                  className="primary"
                  bg="#155876"
                  style={{
                    fontSize: 15,
                    marginBottom: 15,
                    width: '100%',
                    padding: 15,
                    fontWeight: 'bold',
                  }}
                  onClick={() =>
                    userPosts && userPosts.current
                      ? userPosts.current.scrollIntoView({
                          block: 'nearest',
                          inline: 'nearest',
                          behavior: 'smooth',
                        })
                      : null
                  }
                />
                <Button
                  title={t('pro.actions.ourReviews')}
                  fontSize={12}
                  icon={
                    <MdComment
                      size={16}
                      color="#fff"
                      style={{ marginRight: 10 }}
                    />
                  }
                  iconPosition="left"
                  className="primary"
                  bg="#155876"
                  style={{
                    fontSize: 15,
                    marginBottom: 15,
                    width: '100%',
                    padding: 15,
                    fontWeight: 'bold',
                  }}
                  onClick={() =>
                    userReviewsRef && userReviewsRef.current
                      ? userReviewsRef.current.scrollIntoView({
                          block: 'nearest',
                          inline: 'nearest',
                          behavior: 'smooth',
                        })
                      : null
                  }
                />
              </Col>
            </Row>
            <Row style={{ paddingTop: 50 }}>
              {description && (
                <>
                  <Col
                    xs={12}
                    sm={12}
                    md={6}
                    style={{
                      padding: 30,
                      border: '1px solid #DDD',
                      borderRadius: 10,
                    }}
                  >
                    <h3>{t('pro.description.title')}</h3>
                    <p style={{ textAlign: 'justify', lineHeight: '25px' }}>
                      {description}
                    </p>
                  </Col>
                  <Col xs={12} sm={12} md={1} style={{ height: 30 }} />
                </>
              )}
              {getHoursAvailable() !== null && (
                <Col
                  xs={12}
                  sm={12}
                  md={5}
                  style={{
                    padding: 30,
                    border: '1px solid #DDD',
                    borderRadius: 10,
                  }}
                >
                  <h3>{t('pro.openingHours.title')}</h3>
                  {getHoursAvailable()}
                </Col>
              )}
            </Row>
            <Row ref={userPosts} style={{ paddingTop: 35 }}>
              <UserPost uid={uid} />
            </Row>
            <Row ref={userContact} style={{ paddingTop: 35 }}>
              <UserContact {...{ isTablet, proUser: pro }} />
            </Row>
            <Row ref={userReviewsRef} style={{ paddingTop: 35 }}>
              <UserReviews uid={uid} />
            </Row>
          </div>
        )}
      </Grid>
    </div>
  );
};

export default ProPage;
